import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { Dispatch } from 'redux';

import { PredefinedMilisecondsTypeEnum } from '../../lib/enums/predefined-miliseconds-type.enum';
import { RootState } from '../../lib/store';
import { getMarketIndexes as storeGetMarketIndexes } from '../../lib/store/fundamental';
import { formatCurrency, formatNumber } from '../../lib/util/DataHelpers';
import { isMarketOpen } from '../../lib/util/MarketDataHelpers';
import { NullableString } from '../../lib/util/types';

import { useIsMobile } from '../../hooks';

import styles from './MarketIndexes.module.scss';

type Item = {
  symbol: string
  changesPercentage: number
  price: number | string
}

type Props = ReduxProps;

const MarketIndexes = (props: Props) => {
  const { indexes, availableIndexes, getMarketIndexes } = props;
  const { isMobile } = useIsMobile();

  useEffect(() => {
    getMarketIndexes();
    let interval;

    if (isMarketOpen()) {
      interval = setInterval(getMarketIndexes, PredefinedMilisecondsTypeEnum.tenSeconds);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isMarketOpen]); //eslint-disable-line

  const getShowName = (symbol: string): NullableString => availableIndexes.find((obj) => obj.symbol === symbol)?.name;

  if (indexes.length === 0) {
    return (
      <div className={styles.indexes}>
        <div className={styles.wrapper}>
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={`index${index.toString()}`} className={styles.card}>
              <div className={classNames(styles.loader, styles.loaderMargin)} />
              <div className={styles.nameAndPercentageWrapper}>
                <div className={styles.loader} />
                <div className={styles.loader} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.indexes}>
      {isMobile && <div className={styles.mobileMargin} />}
      <div className={styles.wrapper}>
        {availableIndexes.map((index) => {
          const item = indexes.find((obj: Item) => obj.symbol === index.symbol) as Item | null | undefined;
          if (item) {
            return (
              <div key={item.symbol} className={styles.card}>
                {isMobile ? (
                  <>
                    <div className={styles.nameAndPercentageWrapper}>
                      <div className={styles.name}>
                        {getShowName(item.symbol)}
                      </div>
                    </div>
                    {item.changesPercentage > 0 ? (
                      <div className={styles.positivePercentage}>
                        +
                        {formatNumber(item.changesPercentage)}
                        %
                      </div>
                    ) : (
                      <div className={styles.negativePercentage}>
                        {formatNumber(item.changesPercentage)}
                        %
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className={styles.nameAndPercentageWrapper}>
                      <div className={styles.name}>
                        {getShowName(item.symbol)}
                      </div>
                    </div>
                    <div className={styles.nameAndPercentageWrapper}>
                      <div className={styles.price}>
                        {formatCurrency(item.price)}
                      </div>
                      {item.changesPercentage > 0 ? (
                        <div className={styles.positivePercentage}>
                          +
                          {formatNumber(item.changesPercentage)}
                          %
                        </div>
                      ) : (
                        <div className={styles.negativePercentage}>
                          {formatNumber(item.changesPercentage)}
                          %
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
      {isMobile && <div className={styles.mobileMargin} />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  availableIndexes: state.fundamental.availableIndexes,
  indexes: state.fundamental.marketIndexes,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getMarketIndexes: () => dispatch(storeGetMarketIndexes()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(MarketIndexes);
